import { useState, useCallback } from 'react';
import { UpdateUserProfileData } from '@/types';
import { updateUserProfile, getUserProfile } from '@/services';
import { uploadProfilePhoto } from '@/services';

export interface UseProfileReturn {
  loading: boolean;
  error: string | null;
  updateProfile: (userId: string, data: UpdateUserProfileData) => Promise<void>;
  uploadPhoto: (userId: string, file: File) => Promise<string>;
  refreshProfile: (userId: string) => Promise<void>;
}

/**
 * Custom hook for user profile management
 */
export const useProfile = (): UseProfileReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateProfile = async (
    userId: string,
    data: UpdateUserProfileData
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await updateUserProfile(userId, data);
    } catch (err) {
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (userId: string, file: File): Promise<string> => {
    try {
      setLoading(true);
      setError(null);

      const photoURL = await uploadProfilePhoto(userId, file);

      // Update user profile with new photo URL
      await updateUserProfile(userId, { photoURL });

      return photoURL;
    } catch (err) {
      setError('Failed to upload photo');
      console.error('Error uploading photo:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = useCallback(async (userId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await getUserProfile(userId);
    } catch (err) {
      setError('Failed to refresh profile');
      console.error('Error refreshing profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    updateProfile,
    uploadPhoto,
    refreshProfile,
  };
};
